import { BElement } from "utils/base-component"

export class MobileMenu {
  private cssClass: string
  private navHeader?: BElement
  private logoContainer?: BElement
  private menuBtn?: BElement
  private menuNav?: BElement
  private mobSearchBtn?: BElement
  private searchContainer?: BElement
  private searchForm?: BElement
  private searchInput?: BElement
  private windowWidth = 0

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.initializeElements()
    this.addEventListeners()
  }

  private initializeElements() {
    const getElement = (selector: string) => {
      const element = document.querySelector(selector)
      return element ? new BElement(element, this.cssClass) : undefined
    }

    this.navHeader = getElement(".page-header__nav-header")
    this.logoContainer = getElement(".page-header__logo-container")
    this.menuBtn = getElement(".page-header__button--mobile-menu")
    this.menuNav = getElement(".menu-nav")
    this.mobSearchBtn = getElement(".location-search-header-text-field__mob-search-button")
    this.searchContainer = getElement(".location-search-header-text-field")
    this.searchForm = getElement(".location-search-header-text-field__search-form")
    this.searchInput = getElement(".location-search-header-text-field__search-input")
  }

  private addEventListeners() {
    this.menuBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleMenu(this.menuNav, "block", this.menuBtn)
    })

    this.mobSearchBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.openSearch()
    })

    // Add document click listener to close search when clicking outside
    document.addEventListener("click", (e) => {
      const target = e.target as Node
      if (
        this.searchContainer?.element &&
        this.searchContainer.element.classList.contains("search-active") &&
        !this.searchContainer.element.contains(target) &&
        !this.mobSearchBtn?.element.contains(target)
      ) {
        this.closeSearch()
      }
    })

    // special case for reveal search button as it only shows on smallest screen sizes
    this.windowWidth = window.innerWidth
    window.addEventListener("resize", () => {
      if (this.windowWidth === window.innerWidth) return

      this.windowWidth = window.innerWidth
      if (!this.isCompactSearchMode()) {
        this.closeSearch()
      }
    })

    // Handle window resize to close mobile menus when switching to desktop
    window.addEventListener("resize", () => {
      if (window.innerWidth >= 948) {
        this.closeAllMobileMenus()
      }
    })

    this.closeSearch()
  }

  private isCompactSearchMode(): boolean {
    if (!this.mobSearchBtn?.element) return false
    return window.getComputedStyle(this.mobSearchBtn.element).display !== "none"
  }

  private openSearch(): void {
    this.searchContainer?.element.classList.add("search-active")
    this.logoContainer?.element.classList.add("search-active")
    this.mobSearchBtn?.element.classList.add("active")
    this.mobSearchBtn?.element.setAttribute("aria-expanded", "true")
    this.searchForm?.element.setAttribute("aria-hidden", "false")
    this.searchInput?.element.focus()
  }

  private closeSearch(): void {
    this.searchContainer?.element.classList.remove("search-active")
    this.logoContainer?.element.classList.remove("search-active")
    this.mobSearchBtn?.element.classList.remove("active")
    this.mobSearchBtn?.element.setAttribute("aria-expanded", "false")
    this.searchForm?.element.setAttribute(
      "aria-hidden",
      this.isCompactSearchMode() ? "true" : "false",
    )
  }

  // Methods to control sticky footer ad visibility
  private static hideStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = "none"
    }
  }

  private static showStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = ""
    }
  }

  // Helper method to toggle a specific menu
  private toggleMenu(menu?: BElement, displayType: string = "block", button?: BElement): void {
    if (!menu?.element) return

    const menuElement = menu.element
    const currentDisplay = menu.element.style.display
    const navHeader = this.navHeader?.element
    // Check if this menu is already visible
    const isVisible = currentDisplay !== "none" && currentDisplay !== ""

    // For all other menus
    if (isVisible) {
      // Only set display: none for mobile-specific menus, not shared dropdowns
      if (menuElement.classList.contains("menu-nav")) {
        menuElement.style.display = "none"
        // Remove mm-active class from nav-header when menu is closed
        if (navHeader) {
          navHeader.classList.remove("mm-active")
        }
      }
      // Remove active class from menu if present
      if (menuElement.classList.contains("active")) {
        menuElement.classList.remove("active")
      }
      // Allow body scrolling when menu is hidden
      document.body.style.overflow = ""
      document.body.style.position = "relative"
      // Show sticky ad when menu is closed
      if (menuElement.classList.contains("menu-nav")) {
        MobileMenu.showStickyAd()
      }
      // Also toggle aria-expanded state on buttons if present
      if (button && button.element) {
        // Set aria-expanded to false
        if (button.element.hasAttribute("aria-expanded")) {
          button.element.setAttribute("aria-expanded", "false")
        }
        // Remove active class from button if present
        if (button?.element.classList.contains("active")) {
          button.element.classList.remove("active")
        }
      }
    } else {
      // Close all menus first
      this.closeAllMobileMenus()
      // Then show this menu
      menuElement.style.display = displayType
      // Add active class to menu
      menuElement.classList.add("active")
      // Prevent body scrolling when full-screen menu is shown
      // Only apply to full-screen menu (nav containers)
      if (menuElement.classList.contains("menu-nav")) {
        document.body.style.overflow = "hidden"
        document.body.style.position = "fixed"
        // Hide sticky ad when mobile menu is active
        MobileMenu.hideStickyAd()
        // Add mm-active class to nav-header when menu is open
        if (navHeader) {
          navHeader.classList.add("mm-active")
        }
      }
      // Also toggle the button states if provided
      if (button?.element) {
        // Set aria-expanded to true if the attribute exists
        if (button.element.hasAttribute("aria-expanded")) {
          button.element.setAttribute("aria-expanded", "true")
        }
        // Add active class to button
        button.element.classList.add("active")
      }
    }
  }

  // Helper to close all mobile menus with an optional exception
  closeAllMobileMenus(exceptMenu?: BElement): void {
    const menus = [this.menuNav]
    // flag to track if a fullscreen menu is open
    let menuOpen = false
    const navHeader = this.navHeader?.element
    // create a mapping between menus and their toggle buttons
    const menuButtonMap = new Map([[this.menuNav, this.menuBtn]])

    menus.forEach((menu) => {
      // Skip this menu if it's the exception
      if (exceptMenu && menu === exceptMenu) {
        // If the exception is a full-screen menu and it will remain open, set the flag to true
        if (menu.element && menu.element.classList.contains("menu-nav")) {
          menuOpen = true
        }
        return
      }

      if (menu && menu.element) {
        const menuElement = menu.element
        // Only set display: none for mobile-specific menus, not shared dropdowns
        if (menuElement.classList.contains("menu-nav")) {
          menuElement.style.display = "none"
        }

        // Remove active class if present
        if (menuElement.classList.contains("active")) {
          menuElement.classList.remove("active")
        }

        // Get the corresponding button for this menu
        const button = menuButtonMap.get(menu)

        // Reset aria-expanded and active class for the button if applicable
        if (button && button.element) {
          // Reset aria-expanded attribute if it exists
          if (button.element.hasAttribute("aria-expanded")) {
            button.element.setAttribute("aria-expanded", "false")
          }

          // Remove active class from button if present
          if (button.element.classList.contains("active")) {
            button.element.classList.remove("active")
          }
        }
      }
    })

    // If no full-screen menu is open, restore body scrolling
    if (!menuOpen) {
      document.body.style.overflow = ""
      document.body.style.position = "relative"
      // Show sticky ad when all menus are closed
      MobileMenu.showStickyAd()
      // Remove mm-active class from nav-header when all menus are closed
      if (navHeader) {
        navHeader.classList.remove("mm-active")
      }
    }
  }

  // Public method to check if click is inside menus (for document click handler)
  isMenuClick(target: Node): boolean {
    return !!(
      (this.menuNav?.element && this.menuNav.element.contains(target)) ||
      (this.searchForm?.element && this.searchForm.element.contains(target)) ||
      (this.searchInput?.element && this.searchInput.element.contains(target))
    )
  }

  // Public method to check if click is on menu buttons (for document click handler)
  isMenuButtonClick(target: Node): boolean {
    return !!(
      (this.menuBtn?.element && this.menuBtn.element.contains(target)) ||
      (this.mobSearchBtn?.element && this.mobSearchBtn.element.contains(target))
    )
  }

  // Public method to check if any mobile menus are currently open
  hasOpenMenus(): boolean {
    return !!(
      (this.menuNav?.element && this.menuNav.element.classList.contains("active")) ||
      (this.searchContainer?.element &&
        this.searchContainer.element.classList.contains("search-active"))
    )
  }
}
